import React, { useState, useEffect } from "react";
import {
  Box, Typography, Grid, Checkbox, CircularProgress,
  Button, Chip, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Select, MenuItem,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ExtensionIcon from "@mui/icons-material/Extension";
import LinkIcon from "@mui/icons-material/Link";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { axiosConfig } from "../../axios/axiosConfig";
import toast from "react-hot-toast";

// ── Map Dialog ────────────────────────────────────────────────────────────────
function MapDialog({ open, plan, moduleType, onClose }) {
  const [modules,       setModules]       = useState([]);
  const [moduleLoading, setModuleLoading] = useState(false);
  const [checked,       setChecked]       = useState(new Set());
  const [preTagged,     setPreTagged]     = useState(new Set()); // modules already tagged on server
  const [orderMap,      setOrderMap]      = useState({}); // { modId: orderValue }
  const [submitting,    setSubmitting]    = useState(false);
  const [rowEditActive, setRowEditActive] = useState({}); // {modId: bool}
  const [rowModMap,     setRowModMap]     = useState({}); // {modId: newModId}

  const isMain = moduleType === "main";

  useEffect(() => {
    if (!open) return;
    setChecked(new Set());
    setPreTagged(new Set());
    setOrderMap({});
    setRowEditActive({});
    setRowModMap({});
    setModules([]);
    setModuleLoading(true);
    const mainFlag = moduleType === "main";
    const planCode = plan?.plan_code;
    const allUrl   = mainFlag ? "home/home-page-module/"                                          : "home/home-page-sub-module/";
    const tagUrl   = mainFlag ? `home/home-page-module-tag-with-plan/?plan_code=${planCode}`      : `home/home-page-sub-module-tag-with-plan/?plan_code=${planCode}`;
    const idKey    = mainFlag ? "home_page_module"                                                : "home_page_sub_module";

    Promise.all([
      axiosConfig.get(allUrl),
      planCode ? axiosConfig.get(tagUrl) : Promise.resolve({ data: { response: [] } }),
    ])
      .then(([modRes, tagRes]) => {
        const mods    = modRes.data?.response || [];
        setModules(mods);
        const tagged  = tagRes.data?.response || tagRes.data?.data || [];
        const initialChecked = new Set();
        const initialOrderMap = {};
        tagged.forEach((t) => {
          let id = Number(t[idKey]);
          if (isNaN(id) || id <= 0) {
            Object.values(t).forEach((val) => {
              const n = Number(val);
              if (!isNaN(n) && n > 0) id = id > 0 ? id : n;
            });
          }
          if (!isNaN(id) && id > 0) {
            initialChecked.add(id);
            if (t.plan_based_order) initialOrderMap[id] = String(t.plan_based_order);
          }
        });
        setChecked(initialChecked);
        setPreTagged(new Set(initialChecked));
        setOrderMap(initialOrderMap);
      })
      .catch(() => toast.error(`Failed to load ${mainFlag ? "main" : "sub"} modules`))
      .finally(() => setModuleLoading(false));
  }, [open, plan?.plan_code, moduleType]); // eslint-disable-line

  const toggleCheck = (id) => {
    const nid = Number(id);
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(nid) ? next.delete(nid) : next.add(nid);
      return next;
    });
  };

  const toggleAll = () =>
    setChecked(
      checked.size === modules.length
        ? new Set()
        : new Set(modules.map((m) => Number(m.id)))
    );

  const handleSubmit = async () => {
    if (!plan) { toast.error("Please select a plan first"); return; }

    const url = isMain
      ? "home/home-page-module-tag-with-plan/"
      : "home/home-page-sub-module-tag-with-plan/";

    // Rows being edited (PUT) — preTagged rows that user opened with edit icon
    const editedIds = Object.keys(rowEditActive)
      .filter((id) => rowEditActive[id])
      .map(Number);

    // Newly selected rows (POST) — checked but not preTagged
    const newIds = Array.from(checked).filter((id) => !preTagged.has(id));

    if (editedIds.length === 0 && newIds.length === 0) {
      toast.error("No changes to save"); return;
    }

    // Validate order for edited rows
    for (const id of editedIds) {
      const o = Number(orderMap[id]);
      if (!orderMap[id] || isNaN(o) || o <= 0) {
        toast.error("Order must be > 0 for all edited modules"); return;
      }
    }

    // Validate order for new rows
    for (const id of newIds) {
      const o = Number(orderMap[id]);
      if (!orderMap[id] || isNaN(o) || o <= 0) {
        toast.error("Order must be > 0 for all selected modules"); return;
      }
    }
    const newOrderVals = newIds.map((id) => Number(orderMap[id]));
    if (new Set(newOrderVals).size !== newOrderVals.length) {
      toast.error("Duplicate order values are not allowed"); return;
    }

    setSubmitting(true);
    try {
      // PUT — update already-tagged modules
      if (editedIds.length > 0) {
        const moduleList = editedIds.map((id) => ({
          id: rowModMap[id] ?? id,
          plan_based_order: Number(orderMap[id]),
        }));
        const payload = isMain
          ? { plan_code: plan.plan_code, module: moduleList }
          : { plan_code: plan.plan_code, sub_module: moduleList };
        await axiosConfig.put(url, payload);
      }

      // POST — tag new modules
      if (newIds.length > 0) {
        const moduleList = newIds.map((id) => ({
          id: rowModMap[id] ?? id,
          plan_based_order: Number(orderMap[id]),
        }));
        const payload = isMain
          ? { plan_code: plan.plan_code, module: moduleList }
          : { plan_code: plan.plan_code, sub_module: moduleList };
        const res = await axiosConfig.post(url, payload);
        toast.success(res.data?.message || "Modules tagged successfully");
      } else {
        toast.success("Updated successfully");
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}>
      <DialogTitle sx={{
        background: "#04343a", color: "#fff", fontWeight: 700, fontSize: "1rem",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        py: 2, px: 3,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isMain
            ? <ViewModuleIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.8)" }} />
            : <ExtensionIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.8)" }} />}
          Map {isMain ? "Main" : "Sub"} Modules — {plan?.name || plan?.plan_code}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.7)" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {moduleLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} sx={{ color: "#016551" }} />
          </Box>
        ) : modules.length === 0 ? (
          <Typography sx={{ color: "#94a3b8", fontSize: 13, textAlign: "center", py: 5 }}>
            No modules found
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 380 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", background: "#f8fafc", py: 1, px: 1.5, width: 36 }}>
                    <Checkbox size="small"
                      checked={checked.size === modules.length && modules.length > 0}
                      indeterminate={checked.size > 0 && checked.size < modules.length}
                      onChange={toggleAll}
                      sx={{ color: "#016551", p: 0, "&.Mui-checked": { color: "#016551" }, "&.MuiCheckbox-indeterminate": { color: "#016551" } }}
                    />
                  </TableCell>
                  {["#", "Module Name", "Plan Code", "Order *", "Status", "Action"].map((h) => (
                    <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", background: "#f8fafc", py: 1, px: 1.5, whiteSpace: "nowrap" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map((mod, i) => {
                  const modId     = Number(mod.id);
                  const isChecked = checked.has(modId);
                  const isEditing = !!rowEditActive[modId];
                  return (
                    <TableRow key={mod.id} onClick={() => toggleCheck(mod.id)}
                      sx={{ cursor: "pointer",
                        background: isChecked ? "#f0fdf4" : "transparent",
                        "&:hover": { background: isChecked ? "#f0fdf4" : "#f8fafc" },
                      }}>
                      {/* Checkbox */}
                      <TableCell sx={{ py: 1, px: 1.5 }}>
                        <Checkbox size="small" checked={isChecked}
                          onChange={() => toggleCheck(mod.id)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ color: "#016551", "&.Mui-checked": { color: "#016551" }, p: 0 }} />
                      </TableCell>
                      {/* # */}
                      <TableCell sx={{ py: 1, px: 1.5, color: "#94a3b8", fontSize: 12 }}>{i + 1}</TableCell>
                      {/* Module Name — dropdown when editing */}
                      <TableCell sx={{ py: 0.5, px: 1.5 }} onClick={(e) => e.stopPropagation()}>
                        {isEditing ? (
                          <Select
                            value={rowModMap[modId] ?? modId}
                            onChange={(e) => setRowModMap((prev) => ({ ...prev, [modId]: Number(e.target.value) }))}
                            size="small"
                            sx={{ fontSize: 12, width: 170, height: 30,
                              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#016551" },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#016551" },
                            }}>
                            {modules.map((m) => (
                              <MenuItem key={m.id} value={Number(m.id)} sx={{ fontSize: 12 }}>{m.name}</MenuItem>
                            ))}
                          </Select>
                        ) : (
                          <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>
                            {modules.find((m) => Number(m.id) === (rowModMap[modId] ?? modId))?.name || mod.name}
                          </Typography>
                        )}
                      </TableCell>
                      {/* Plan Code */}
                      <TableCell sx={{ py: 1, px: 1.5 }}>
                        <Chip label={plan?.plan_code || "—"} size="small"
                          sx={{ background: "#f1f5f9", color: "#374151", fontWeight: 600, fontSize: 11, height: 22, borderRadius: "6px" }} />
                      </TableCell>
                      {/* Order */}
                      <TableCell sx={{ py: 0.5, px: 1.5 }} onClick={(e) => e.stopPropagation()}>
                        {(isEditing || (isChecked && !preTagged.has(modId))) ? (
                          <TextField
                            value={orderMap[modId] || ""}
                            onChange={(e) => setOrderMap((prev) => ({ ...prev, [modId]: e.target.value }))}
                            type="number" size="small" inputProps={{ min: 1 }} placeholder="0"
                            sx={{ width: 70,
                              "& .MuiOutlinedInput-root": { fontSize: 12, borderRadius: "6px",
                                "& fieldset": { borderColor: "#016551" },
                                "&:hover fieldset": { borderColor: "#016551" },
                                "&.Mui-focused fieldset": { borderColor: "#016551" },
                              },
                              "& input": { py: 0.5, px: 1 },
                            }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 12, color: orderMap[modId] ? "#0f172a" : "#94a3b8" }}>
                            {orderMap[modId] || "—"}
                          </Typography>
                        )}
                      </TableCell>
                      {/* Status */}
                      <TableCell sx={{ py: 1, px: 1.5 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: mod.is_active ? "#16a34a" : "#dc2626" }}>
                          {mod.is_active ? "Active" : "Inactive"}
                        </Typography>
                      </TableCell>
                      {/* Action — edit only for already-tagged modules */}
                      <TableCell sx={{ py: 1, px: 1, whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                        {preTagged.has(modId) && (
                          isEditing ? (
                            <IconButton size="small"
                              onClick={(e) => { e.stopPropagation(); setRowEditActive((prev) => ({ ...prev, [modId]: false })); }}
                              sx={{ color: "#dc2626", "&:hover": { background: "#fef2f2" } }}>
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          ) : (
                            <IconButton size="small"
                              onClick={(e) => { e.stopPropagation(); setRowEditActive((prev) => ({ ...prev, [modId]: true })); setRowModMap((prev) => ({ ...prev, [modId]: prev[modId] ?? modId })); }}
                              sx={{ color: "#016551", "&:hover": { background: "#f0fdf4" } }}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!moduleLoading && modules.length > 0 && checked.size > 0 && (
          <Box sx={{ px: 2, py: 1, borderTop: "1px solid #f1f5f9" }}>
            <Chip label={`${checked.size} selected`} size="small"
              sx={{ background: "#dcfce7", color: "#16a34a", fontWeight: 700, fontSize: 11, height: 22, borderRadius: "6px" }} />
          </Box>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{
          textTransform: "none", fontWeight: 600, color: "#64748b",
          border: "1px solid #e2e8f0", borderRadius: "8px", px: 2.5,
          "&:hover": { background: "#f8fafc" },
        }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}
          disabled={submitting || (Array.from(checked).every(id => preTagged.has(id)) && !Object.values(rowEditActive).some(Boolean))}
          sx={{
            textTransform: "none", fontWeight: 700, borderRadius: "8px", px: 3,
            background: "linear-gradient(135deg, #016551, #014d3d)",
            boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
            "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)", boxShadow: "0 6px 16px rgba(1,101,81,0.4)" },
            "&:disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
          }}>
          {submitting && <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />}
          {submitting ? "Saving…" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── View Dialog ───────────────────────────────────────────────────────────────
function ViewDialog({ open, plan, moduleType, onClose }) {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const isMain = moduleType === "main";

  useEffect(() => {
    if (!open) return;
    setRows([]);
    setLoading(true);
    const tagUrl    = isMain ? "home/home-page-module-tag-with-plan/"  : "home/home-page-sub-module-tag-with-plan/";
    const moduleUrl = isMain ? "home/home-page-module/"                : "home/home-page-sub-module/";
    const idKey     = isMain ? "home_page_module"                      : "home_page_sub_module";

    Promise.all([axiosConfig.get(tagUrl), axiosConfig.get(moduleUrl)])
      .then(([tagRes, modRes]) => {
        const tags    = tagRes.data?.response || [];
        const modules = modRes.data?.response || [];
        const filtered = plan ? tags.filter((t) => t.plan_code === plan.plan_code) : tags;
        const modMap   = Object.fromEntries(modules.map((m) => [m.id, m]));
        setRows(filtered.map((t) => ({
          tagId:    t.id,
          planCode: t.plan_code,
          isActive: t.is_active,
          module:   modMap[t[idKey]] || null,
          moduleId: t[idKey],
        })));
      })
      .catch(() => toast.error("Failed to load tagged modules"))
      .finally(() => setLoading(false));
  }, [open, plan, moduleType]); // eslint-disable-line

  const cellSx = { fontSize: 12, py: 1, px: 1.5, borderBottom: "1px solid #f1f5f9" };
  const headSx = { fontSize: 11, fontWeight: 700, color: "#64748b", py: 1, px: 1.5, background: "#f8fafc", whiteSpace: "nowrap" };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}>
      <DialogTitle sx={{
        background: "#04343a", color: "#fff", fontWeight: 700, fontSize: "1rem",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        py: 2, px: 3,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <VisibilityIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.8)" }} />
          {isMain ? "Main" : "Sub"} Modules{plan ? ` — ${plan.name || plan.plan_code}` : ""}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.7)" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} sx={{ color: "#016551" }} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography sx={{ color: "#94a3b8", fontSize: 13, textAlign: "center", py: 5 }}>
            No modules tagged{plan ? " to this plan" : ""}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["#", "Module Name", "Plan Code", "Status"].map((h) => (
                    <TableCell key={h} sx={headSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={row.tagId} sx={{ "&:hover": { background: "#fafafa" } }}>
                    <TableCell sx={{ ...cellSx, color: "#94a3b8", width: 36 }}>{i + 1}</TableCell>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>
                        {row.module?.name || `Module #${row.moduleId}`}
                      </Typography>
                      {row.module?.sub_title && (
                        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{row.module.sub_title}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Chip label={row.planCode} size="small"
                        sx={{ background: "#f1f5f9", color: "#374151", fontWeight: 600, fontSize: 11, height: 22, borderRadius: "6px" }} />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: row.isActive ? "#16a34a" : "#dc2626" }}>
                        {row.isActive ? "Active" : "Inactive"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 2.5, py: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{
          textTransform: "none", fontWeight: 600, borderRadius: "8px", px: 3,
          background: "#04343a", "&:hover": { background: "#016551" },
        }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Module Action Card (reused for Main & Sub) ────────────────────────────────
function ModuleActionCard({ title, icon, plan, moduleType, onMap, onView }) {
  return (
    <Box sx={{
      borderRadius: "10px", border: "1px solid #e2e8f0",
      overflow: "hidden", flex: 1,
    }}>
      {/* Card header */}
      <Box sx={{
        background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
        px: 2, py: 1.25,
        display: "flex", alignItems: "center", gap: 1,
      }}>
        <Box sx={{ color: "#04343a", display: "flex" }}>{icon}</Box>
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#04343a" }}>
          {title}
        </Typography>
      </Box>

      {/* Buttons */}
      <Box sx={{ p: 1.5, display: "flex", gap: 1 }}>
        <Button
          variant="contained"
          startIcon={<LinkIcon fontSize="small" />}
          onClick={onMap}
          fullWidth
          sx={{
            textTransform: "none", fontWeight: 600, fontSize: 12,
            borderRadius: "8px", py: 1,
            background: "linear-gradient(135deg, #016551, #014d3d)",
            boxShadow: "0 2px 8px rgba(1,101,81,0.25)",
            "&:hover": {
              background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
              boxShadow: "0 4px 12px rgba(1,101,81,0.4)",
            },
            "&:disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
          }}
        >
          Map
        </Button>
        <Button
          startIcon={<VisibilityIcon fontSize="small" />}
          onClick={onView}
          fullWidth
          sx={{
            textTransform: "none", fontWeight: 600, fontSize: 12,
            borderRadius: "8px", py: 1,
            border: "1px solid #e2e8f0", color: "#374151",
            "&:hover": { background: "#f1f5f9", border: "1px solid #cbd5e1" },
            "&:disabled": { border: "1px solid #f1f5f9", color: "#cbd5e1" },
          }}
        >
          View
        </Button>
      </Box>
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ModuleMapping() {
  const [plans,        setPlans]        = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  // { plan, moduleType } — which plan+type the dialog targets
  const [mapDialog,  setMapDialog]  = useState({ open: false, plan: null, moduleType: null });
  const [viewDialog, setViewDialog] = useState({ open: false, plan: null, moduleType: null });

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosConfig({ method: "get", url: "/payment/plans" });
        const list = res?.data?.response?.data || res?.data?.data || [];
        setPlans(list);
      } catch {
        toast.error("Failed to load plans");
      } finally {
        setPlansLoading(false);
      }
    })();
  }, []);

  const btnSx = () => ({
    textTransform: "none", fontWeight: 600, fontSize: 10,
    borderRadius: "4px", py: 0, px: 0.75, minWidth: 0, height: 24,
    border: "1.5px solid #016551", color: "#016551", background: "transparent",
    "& .MuiButton-startIcon": { mr: 0.4 },
    "&:hover": { background: "#f0fdf4" },
  });

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>

      {/* Header */}
      <Box sx={{
        background: "#04343a", borderRadius: "12px", p: 3, mb: 3,
        boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
        display: "flex", alignItems: "center", gap: 2,
      }}>
        <AccountTreeIcon sx={{ color: "#ffffff", fontSize: 32 }} />
        <Box>
          <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: "1.25rem" }}>Module Mapping</Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem" }}>Tag modules to subscription plans</Typography>
        </Box>
      </Box>

      {/* Plan list card */}
      <Box sx={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>

        {/* Table header */}
        <Box sx={{ background: "#04343a", px: 2.5, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>Subscription Plans</Typography>
          {!plansLoading && (
            <Chip label={`${plans.length} plans`} size="small"
              sx={{ background: "rgba(255,255,255,0.15)", color: "#fff", fontWeight: 600, fontSize: 11, height: 20 }} />
          )}
        </Box>

        {/* Column labels */}
        <Box sx={{ display: "flex", alignItems: "center", px: 2.5, py: 1, background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <Typography sx={{ flex: 1, fontSize: 11, fontWeight: 700, color: "#64748b" }}>Plan</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", width: 160, textAlign: "center" }}>Main Module</Typography>
          <Box sx={{ width: 24 }} />
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", width: 160, textAlign: "center" }}>Sub Module</Typography>
        </Box>

        {/* Rows */}
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          {plansLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} sx={{ color: "#016551" }} />
            </Box>
          ) : plans.length === 0 ? (
            <Typography sx={{ color: "#94a3b8", fontSize: 13, textAlign: "center", py: 3 }}>No plans found</Typography>
          ) : plans.map((plan) => (
            <Box key={plan.plan_code} sx={{
              display: "flex", alignItems: "center",
              px: 2, py: 1.25, borderRadius: "8px",
              border: "1px solid #e2e8f0", background: "#fafafa",
              "&:hover": { background: "#f1f5f9" },
            }}>
              {/* Plan info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#0f172a", lineHeight: 1.3 }}>
                  {plan.name || plan.plan_code}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3 }}>
                  <Typography sx={{ fontSize: 11, color: "#64748b" }}>{plan.plan_code}</Typography>
                  {plan.price != null && (
                    <Chip icon={<CurrencyRupeeIcon sx={{ fontSize: "11px !important" }} />}
                      label={plan.price} size="small"
                      sx={{ background: "#f1f5f9", color: "#374151", fontWeight: 700, fontSize: 11, height: 20, borderRadius: "6px" }} />
                  )}
                </Box>
              </Box>

              {/* Main Module buttons */}
              <Box sx={{ display: "flex", gap: 0.75, width: 160, justifyContent: "center" }}>
                <Button size="small"
                  startIcon={<LinkIcon sx={{ fontSize: "11px !important" }} />}
                  onClick={() => setMapDialog({ open: true, plan, moduleType: "main" })}
                  sx={btnSx()}>Map</Button>
                <Button size="small"
                  startIcon={<VisibilityIcon sx={{ fontSize: "11px !important" }} />}
                  onClick={() => setViewDialog({ open: true, plan, moduleType: "main" })}
                  sx={btnSx()}>View</Button>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ mx: 1.5, width: 24 }} />

              {/* Sub Module buttons */}
              <Box sx={{ display: "flex", gap: 0.75, width: 160, justifyContent: "center" }}>
                <Button size="small"
                  startIcon={<LinkIcon sx={{ fontSize: "11px !important" }} />}
                  onClick={() => setMapDialog({ open: true, plan, moduleType: "sub" })}
                  sx={btnSx()}>Map</Button>
                <Button size="small"
                  startIcon={<VisibilityIcon sx={{ fontSize: "11px !important" }} />}
                  onClick={() => setViewDialog({ open: true, plan, moduleType: "sub" })}
                  sx={btnSx()}>View</Button>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Dialogs */}
      <MapDialog
        open={mapDialog.open}
        plan={mapDialog.plan}
        moduleType={mapDialog.moduleType}
        onClose={() => setMapDialog({ open: false, plan: null, moduleType: null })}
      />
      <ViewDialog
        open={viewDialog.open}
        plan={viewDialog.plan}
        moduleType={viewDialog.moduleType}
        onClose={() => setViewDialog({ open: false, plan: null, moduleType: null })}
      />
    </Box>
  );
}
